import React, { useEffect, useMemo, useState, Fragment } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Dialog, Transition } from "@headlessui/react";
import { toast } from "react-toastify";
import Avatar from "../../component/Avatar";
import sponsorApi from "../../apis/organiserApis/sponsor.api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faXmark,
  faTrash,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";

const BRAND = "#1d275f";

function SkeletonRow() {
  return (
    <div className="animate-pulse p-4 border-b flex items-center gap-3">
      <div className="h-12 w-12 rounded-full bg-gray-200" />
      <div className="flex-1 space-y-2">
        <div className="h-4 bg-gray-200 rounded w-3/4" />
        <div className="h-3 bg-gray-200 rounded w-1/2" />
      </div>
      <div className="h-8 w-20 rounded bg-gray-200" />
    </div>
  );
}

function EmptyState({
  title = "No invitations",
  helper = "No outgoing invitations found.",
}) {
  return (
    <div className="py-12 text-center text-gray-500">
      <div className="mb-3 text-lg font-semibold text-gray-700">{title}</div>
      <div className="text-sm">{helper}</div>
    </div>
  );
}

export default function OutgoingInvites() {
  const navigate = useNavigate();
  const { campaignId: routeCampaignId } = useParams();
  const campaignId = routeCampaignId ? Number(routeCampaignId) : null;

  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [pagination, setPagination] = useState(null);
  const [error, setError] = useState(null);

  const [canceling, setCanceling] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);

  const fetchRequests = (opts = {}) => {
    const { page: p = page } = opts;
    setLoading(true);
    setError(null);

    const successHandler = (res) => {
      const data = res?.data || res || {};
      const arr = data.requests || data || [];
      setRequests(Array.isArray(arr) ? arr : []);
      setPagination(
        data.pagination ||
          data.pagination || {
            current_page: p,
            per_page: limit,
            total_count: Array.isArray(arr) ? arr.length : 0,
            has_more: false,
          }
      );
    };

    const errorHandler = (err) => {
      console.error("Failed to fetch outgoing invites:", err);
      setError("Unable to load invitations. Please try again later.");
      toast.error(
        err?.response?.data?.message || "Failed to load invitations."
      );
    };

    const finalHandler = () => setLoading(false);

    sponsorApi.listOutgoingInvites({
      params: {
        page: p,
        limit,
        ...(campaignId ? { campaignId } : {}),
      },
      success: successHandler,
      error: errorHandler,
      final: finalHandler,
    });
  };

  useEffect(() => {
    fetchRequests({ page: 1 });
  }, [routeCampaignId]);

  const handleCancelClick = (r) => {
    setCancelTarget(r);
    setCanceling(true);
  };

  const doCancel = () => {
    if (!cancelTarget) return;
    const { requestId } = cancelTarget;
    const success = (res) => {
      toast.success("Invitation canceled.");

      setRequests((prev) => prev.filter((x) => x.requestId !== requestId));
      setCanceling(false);
      setCancelTarget(null);
    };
    const error = (err) => {
      console.error("cancelCampaignInvite error:", err);
      toast.error(
        err?.response?.data?.message || "Failed to cancel invitation."
      );
    };
    const final = () => {};

    sponsorApi.cancelCampaignInvite({
      campaignId: campaignId ?? cancelTarget.campaignId,
      requestId,
      success,
      error,
      final,
    });
  };

  const pages = useMemo(() => {
    if (!pagination) return 1;
    return Math.max(
      1,
      Math.ceil((pagination.total_count || 0) / (pagination.per_page || limit))
    );
  }, [pagination, limit]);

  return (
    <div className="max-w-6xl mx-auto p-4">
      <div className="mb-4 flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            {campaignId
              ? "Outgoing Invitations (Campaign)"
              : "Outgoing Invitations"}
          </h1>
          <p className="text-sm text-gray-500">
            {campaignId
              ? `Invitations sent for campaign #${campaignId}.`
              : "Invitations you have sent across campaigns."}
          </p>
        </div>

        <div className="flex items-center gap-2">
          {campaignId ? (
            <>
              <button
                type="button"
                onClick={() => navigate(`/organizer/campaigns/${campaignId}`)}
                className="px-3 py-2 rounded-xl border bg-white text-sm"
              >
                Back to campaign
              </button>
              <button
                type="button"
                onClick={() =>
                  navigate(`/organizer/campaigns/${campaignId}/invite`)
                }
                className="px-3 py-2 rounded-xl border text-sm bg-[#1d275f] text-white hover:opacity-90 focus:ring-[#1d275f]"
              >
                Send more invites
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => navigate("/organizer/campaigns")}
              className="px-3 py-2 rounded-xl border bg-white text-sm"
            >
              View campaigns
            </button>
          )}
        </div>
      </div>

      <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
        {/* header */}
        <div className="hidden md:grid grid-cols-12 gap-3 px-4 py-3 bg-gray-50 text-xs font-semibold text-gray-600">
          <div className="col-span-3">Sponsor</div>
          <div className="col-span-3">Package</div>
          <div className="col-span-2">Message</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>

        {/* body */}
        {loading ? (
          <>
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
          </>
        ) : requests.length === 0 ? (
          <EmptyState
            title="No outgoing invitations"
            helper="You have not sent any invitations yet."
          />
        ) : (
          requests.map((r) => (
            <div
              key={r.requestId}
              className="grid grid-cols-1 md:grid-cols-12 gap-3 px-4 py-3 items-center border-t"
            >
              {/* Sponsor cell */}
              <div className="md:col-span-3 flex items-center gap-3">
                <Avatar
                  name={r.sponsor_name || "Sponsor"}
                  imageUrl={r.sponsor_logo || undefined}
                  size={44}
                />
                <div className="min-w-0">
                  <div className="font-medium text-gray-900 truncate">
                    {r.sponsor_name || "—"}
                  </div>
                  <div className="text-xs text-gray-400">
                    ID: {r.sponsorId ?? "—"}
                  </div>
                  {r.campaignTitle ? (
                    <div className="text-xs text-gray-500 mt-1">
                      <button
                        onClick={() =>
                          window.location.assign(
                            `/organizer/View-campaign/${r.campaignId}`
                          )
                        }
                        className="text-[color:var(--brand)] underline text-xs"
                        style={{ "--brand": BRAND }}
                      >
                        {r.campaignTitle}
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>

              {/* Package */}
              <div className="md:col-span-3 text-sm text-gray-700">
                {r.invited_pack_name || "—"}
              </div>

              {/* Message */}
              <div className="md:col-span-2 text-sm text-gray-600 truncate">
                {r.invitationMessage || "—"}
              </div>

              {/* Status */}
              <div className="md:col-span-2 text-sm">
                <div
                  className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                    r.requestStatus === "pending"
                      ? "bg-amber-50 text-amber-700"
                      : r.requestStatus === "accepted"
                      ? "bg-green-50 text-green-700"
                      : r.requestStatus === "rejected"
                      ? "bg-red-50 text-red-700"
                      : "bg-gray-100 text-gray-700"
                  }`}
                >
                  {String(r.requestStatus || "").replace("_", " ")}
                </div>
                <div className="text-xs text-gray-400 mt-1">
                  {r.sentAt || "—"}
                </div>
              </div>

              {/* Actions */}
              <div className="md:col-span-2 text-right">
                <div className="flex items-center justify-end gap-2">
                  {r.requestStatus === "pending" ? (
                    <button
                      onClick={() => handleCancelClick(r)}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border text-sm text-rose-700 hover:bg-rose-50"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                      Cancel
                    </button>
                  ) : (
                    <div className="text-xs text-gray-400">—</div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}

        {/* pagination footer */}
        <div className="px-4 py-3 border-t bg-white flex items-center justify-between">
          <div className="text-sm text-gray-600">
            Page{" "}
            <span className="font-semibold">
              {pagination?.current_page ?? 1}
            </span>{" "}
            of <span className="font-semibold">{pages}</span>
          </div>
          <div className="flex gap-2">
            <button
              disabled={loading || (pagination?.current_page ?? 1) <= 1}
              onClick={() => {
                const prev = Math.max(1, (pagination?.current_page ?? 1) - 1);
                setPage(prev);
                fetchRequests({ page: prev });
              }}
              className={`px-3 py-2 rounded-xl border text-sm inline-flex items-center gap-2 ${
                (pagination?.current_page ?? 1) <= 1
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:bg-gray-50"
              }`}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
              Prev
            </button>
            <button
              disabled={loading || (pagination?.current_page ?? 1) >= pages}
              onClick={() => {
                const next = Math.min(
                  pages,
                  (pagination?.current_page ?? 1) + 1
                );
                setPage(next);
                fetchRequests({ page: next });
              }}
              className={`px-3 py-2 rounded-xl border text-sm inline-flex items-center gap-2 ${
                (pagination?.current_page ?? 1) >= pages
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:bg-gray-50"
              }`}
            >
              Next
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        </div>
      </div>

      {/* Cancel confirmation dialog */}
      <Transition appear show={canceling} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => setCanceling(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/30" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-end sm:items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="w-full max-w-md rounded-2xl bg-white shadow-xl border border-gray-100">
                  <div className="px-5 py-4">
                    <Dialog.Title className="text-lg font-semibold text-gray-900">
                      Cancel invitation?
                    </Dialog.Title>
                    <Dialog.Description className="mt-1 text-sm text-gray-500">
                      Are you sure you want to cancel the invitation to{" "}
                      <span className="font-medium">
                        {cancelTarget?.sponsor_name}
                      </span>
                      ? This action cannot be undone.
                    </Dialog.Description>
                  </div>

                  <div className="px-5 pb-4 flex items-center justify-end gap-2">
                    <button
                      onClick={() => {
                        setCanceling(false);
                        setCancelTarget(null);
                      }}
                      className="px-4 py-2 rounded-xl border text-gray-700 hover:bg-gray-50"
                    >
                      Close
                    </button>
                    <button
                      onClick={doCancel}
                      className="px-4 py-2 rounded-xl text-white bg-rose-600 hover:bg-rose-700"
                    >
                      Confirm cancel
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}
